import * as actions from './authActions';
import { Dispatch } from 'redux';
import { getUser } from '../../Api/getUser';
import { getUserFromLS } from '../../utils/getUserFromLS';
import { loginRequest, msalInstance, initializeMsal } from '../../Config/msalConfig';
import { validateMicrosoftToken } from '../../Api/validateMicrosoftToken';
import { completeEnterpriseSSOLogin } from '../../Api/completeEnterpriseSsoLogin';
import { TokenExpiration } from '../../utils/tokenExpiration';
import { getMsalErrorDetails } from '../../utils/msalErrorDetails';
import { sanitizeAxiosError } from '../../utils/logScrubber';
import { CacheService } from '../../Services/CacheService';

// Load user from local storage
export const loadUser = () => async (dispatch: Dispatch<any>) => {
  const user = getUserFromLS();

  if (!user.id) {
    dispatch(actions.authError());
    return;
  }

  // Check if token is expired before making API call
  if (TokenExpiration.isTokenExpired()) {
    TokenExpiration.handleTokenExpiration();
    return;
  }
  
  try {
    getUser(
      (res) => {
        // Monitoring will be started by authReducer after successful user load
        dispatch(actions.userLoaded(res));
      },
      (err) => {
        dispatch(actions.authError());
      }
    );
  } catch (error) {
    dispatch(actions.authError());
    return;
  }
};

// Ensure MSAL is initialized
let msalInitialized = false;
const ensureMsalInitialized = async () => {
  if (!msalInitialized) {
    await initializeMsal();
    msalInitialized = true;
  }
};

const MSFT_LOGIN_EMAIL_KEY = 'msftLoginEmail';

export { MSFT_LOGIN_EMAIL_KEY };

// Microsoft SSO login - Redirect method
export const loginWithMicrosoftRedirect = (email: string) => async (dispatch: Dispatch<any>) => {
  dispatch(actions.setAuthLoading());
  
  try {
    await ensureMsalInitialized();
    if (email) {
      sessionStorage.setItem(MSFT_LOGIN_EMAIL_KEY, email);
    }
    await msalInstance.loginRedirect({
      ...loginRequest,
      loginHint: email,
      prompt: 'select_account',
    });
  } catch (error) {
    console.error('Microsoft login failed', {
      stage: 'loginRedirect',
      url: window.location.href,
      msalInitialized,
      accountsCount: msalInstance.getAllAccounts?.().length,
      msal: getMsalErrorDetails(error),
    });
    dispatch(actions.microsoftLoginFailed("Failed to initiate Microsoft login"));
  }
};

// Add a flag to track if we're already processing a redirect
let isProcessingRedirect = false;
let msftRedirectConsumed = false;

// Handle Microsoft redirect
export const handleMicrosoftRedirect = () => async (dispatch: Dispatch<any>) => {
  try {
    if (isProcessingRedirect || msftRedirectConsumed) {
      return;
    }
    
    // Ensure MSAL is initialized
    await ensureMsalInitialized();
    
    // Handle the redirect response
    const response = await msalInstance.handleRedirectPromise();
    
    if (response) {
      if (msftRedirectConsumed) {
        return;
      }
      msftRedirectConsumed = true;
      // Set flag to true
      isProcessingRedirect = true;
      dispatch(actions.setAuthLoading());
      
      const enteredEmail = sessionStorage.getItem(MSFT_LOGIN_EMAIL_KEY) || '';

      // Validate the token with your backend
      validateMicrosoftToken(
        response.idToken,
        enteredEmail,
        (userData) => {
          sessionStorage.removeItem(MSFT_LOGIN_EMAIL_KEY);
          // Success - dispatch action with user data
          dispatch(actions.microsoftLoginSuccess(userData));
          
          // Reset flag after successful validation
          isProcessingRedirect = false;
        },
        (error) => {
          console.error('Microsoft login failed', {
            stage: 'validateMicrosoftToken',
            url: window.location.href,
            msalInitialized,
            isProcessingRedirect,
            accountsCount: msalInstance.getAllAccounts?.().length,
            apiError: sanitizeAxiosError(error),
          });
          let errorMessage = "Microsoft login failed";
          const message = error.response?.data["detail"];
          
          // Extract more specific error if available
          if (message) {
            errorMessage = message;
          }
          
          dispatch(actions.microsoftLoginFailed(errorMessage));
          // Reset flag after failed validation
          isProcessingRedirect = false;
        }
      );
    }
  } catch (error) {
    console.error('Microsoft login failed', {
      stage: 'handleRedirectPromise',
      url: window.location.href,
      msalInitialized,
      isProcessingRedirect,
      accountsCount: msalInstance.getAllAccounts?.().length,
      msal: getMsalErrorDetails(error),
    });
    let errorMessage = "Error during Microsoft authentication";
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch(actions.microsoftLoginFailed(errorMessage));
    // Reset flag after error
    isProcessingRedirect = false;
  }
};

// ------------------------------
// Enterprise SSO (OIDC - supports PingFederate, Okta, Auth0, etc.)
// ------------------------------

// Enterprise SSO login - Redirect method (delegated to IdP/Backend URL)
export const loginWithEnterpriseSSORedirect = (email: string) => async (dispatch: Dispatch<any>) => {
  dispatch(actions.setAuthLoading());
  try {
    // Require email so backend can discover tenant and build the OIDC request
    if (!email) {
      throw new Error('Email is required to start Enterprise SSO login');
    }
    const startUrlBase = `${import.meta.env.VITE_API_BASE_URL}/auth/oidc/start`;
    const separator = startUrlBase.includes('?') ? '&' : '?';
    const startUrl = `${startUrlBase}${separator}email=${encodeURIComponent(email)}`;
    // Use replace to avoid back-button loops to the pre-auth page
    window.location.replace(startUrl);
  } catch (error) {
    console.error('Enterprise SSO redirect failed:', error);
    dispatch(actions.enterpriseSSOLoginFailed('Failed to initiate Enterprise SSO login'));
  }
};

// Track if we're processing an Enterprise SSO redirect
let isProcessingEnterpriseRedirect = false;

// Helper: extract parameter from a query string-like source
const getParamFrom = (source: string, key: string): string | null => {
  try {
    const params = new URLSearchParams(source);
    const value = params.get(key);
    return value;
  } catch (_e) {
    return null;
  }
};

// Handle Enterprise SSO redirect (OIDC code flow)
export const handleEnterpriseSSORedirect = () => async (dispatch: Dispatch<any>) => {
  try {
    if (isProcessingEnterpriseRedirect) {
      return;
    }

    // Parse code/state from query params
    const search = window.location.search.startsWith('?') ? window.location.search.substring(1) : window.location.search;
    const codeFromQuery = getParamFrom(search, 'code');
    const stateFromQuery = getParamFrom(search, 'state');

    // OIDC: require code
    if (!codeFromQuery) {
      return; // nothing to process
    }

    isProcessingEnterpriseRedirect = true;
    dispatch(actions.setAuthLoading());

    completeEnterpriseSSOLogin(
      codeFromQuery,
      stateFromQuery,
      (userData) => {
        dispatch(actions.enterpriseSSOLoginSuccess(userData));
        isProcessingEnterpriseRedirect = false;
        window.history.replaceState(null, '', window.location.pathname);
      },
      (error) => {
        console.error('Enterprise SSO code exchange failed:', error);
        const message = error?.response?.data?.detail || error?.message;
        dispatch(actions.enterpriseSSOLoginFailed(message || 'Enterprise SSO login failed'));
        isProcessingEnterpriseRedirect = false;
        window.history.replaceState(null, '', window.location.pathname);
      }
    );
    return;
  } catch (error) {
    console.error('Error handling Enterprise SSO redirect:', error);
    let errorMessage = 'Error during Enterprise SSO authentication';
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    dispatch(actions.enterpriseSSOLoginFailed(errorMessage));
    isProcessingEnterpriseRedirect = false;
  }
};

// Logout - handles both traditional and Microsoft logout
export const logout = () => async (dispatch: Dispatch<any>) => {
  const user = getUserFromLS();
  
  // Stop monitoring token expiration
  TokenExpiration.stopMonitoring();

  // Always clear local auth state, even if external logout flow redirects immediately.
  dispatch(actions.logoutSuccess());

  // Clear persisted cache in background so logout is never blocked by IndexedDB state.
  void CacheService.clearDatabaseData().catch((error) => {
    console.warn('Failed to clear cache during logout:', error);
  });
  
  try {
    // Check if user was authenticated with Microsoft
    if (user.auth_method === 'microsoft') {
      // Ensure MSAL is initialized
      await ensureMsalInitialized();
      
      // Get all accounts
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length > 0) {
        // Logout from Microsoft
        msalInstance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: window.location.origin,
        });
      }
    }
  } catch (error) {
    console.error('Error during logout:', error);
  }
};
